<template>
  <div :class="{ 'new-design': newDesign }">
    <div
      v-if="label"
      class="field-heading q-mb-sm"
    >
      {{ label }}
    </div>

    <div class="row align-items justify-between">
      <div v-if="showDistributionListDropdown">
        <BaseButtonDropdown
          chevron
          close-on-click
          color="white"
          dense
          :disabled="disabled"
          :label="$t('distributed_email_input.distribution_dropdown_label')"
          new-design
          placement="bottom-start"
        >
          <InputText
            v-model="listFilter"
            auto-focus
            borderless
            clearable
            debounce="300"
            :new-design="newDesign"
            :placeholder="
              $t('distributed_email_input.search_input_placeholder')
            "
          >
            <template #prepend>
              <QIcon
                color="grey"
                name="search"
                size="16px"
              />
            </template>
          </InputText>
          <div class="dropdown-list scroll-y">
            <span
              v-for="list in visibleDistributionLists"
              :key="list.id"
              class="item"
              @click="addFromDistributionList(list)"
            >
              <strong>
                {{ list.name }}
                <span
                  v-if="list.restricted"
                  class="tag label inline-block text-white"
                >
                  {{ $t("distributed_email_input.restricted_label") }}
                </span>
              </strong>
              <small>
                {{ list.recipients[0] }}
                <template v-if="list.recipients.length > 1">
                  {{
                    $t("distributed_email_input.recipient_subtext", {
                      listRecipientsLength: list.recipients.length - 1,
                    })
                  }}
                </template>
              </small>
            </span>
          </div>
        </BaseButtonDropdown>
      </div>
      <BaseButton
        v-if="recipients.length"
        flat
        icon="ion-md-backspace"
        padding="none"
        @click="clearRecipients()"
      >
        <StreemTooltip>{{
          $t("distributed_email_input.clear_all_recipients")
        }}</StreemTooltip>
      </BaseButton>
    </div>

    <div class="row no-wrap send-to-container justify-between">
      <EmailField
        ref="emailField"
        v-model:recipients="recipients"
        class="recipients-email-field"
        :copy-recipients="copyRecipients"
        :csv-export-recipients="csvExportRecipients"
        :disabled="disabled"
        :distribution-lists="distributionLists"
        :new-design="newDesign"
        :user-id="currentUser.id"
        @update:recipients="onUpdateRecipients"
      />
    </div>
    <div
      v-if="validateField && displayError"
      class="field-error q-mt-sm"
    >
      <QIcon name="ion-md-alert" />
      {{ $t("distributed_email_input.please_select_recipient") }}
    </div>
  </div>
</template>

<script>
import { storeToRefs } from "pinia";

import {
  BaseButton,
  BaseButtonDropdown,
  InputText,
} from "shared/components/base";
import EmailField from "shared/components/core/forms/EmailField.vue";
import useTeamPicker from "shared/composables/useTeamPicker";
import { DistributionList } from "shared/resources";
import { useUserStore } from "shared/stores/user";

export default {
  name: "DistributedEmailInput",
  components: {
    InputText,
    BaseButton,
    BaseButtonDropdown,
    EmailField,
  },
  props: {
    modelValue: {
      type: Array,
      required: true,
    },
    showDropdown: {
      type: Boolean,
    },
    validateField: {
      type: Boolean,
    },
    csvExportRecipients: {
      type: Boolean,
    },
    copyRecipients: {
      type: Boolean,
    },
    newDesign: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    label: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue"],
  setup() {
    const userStore = useUserStore();
    const { currentUser } = storeToRefs(userStore);

    const { selectedTeamPreference } = useTeamPicker();

    return { currentUser, selectedTeamPreference };
  },
  data() {
    return {
      displayError: false,
      listFilter: "",
      distributionLists: [],
      recipients: [],
    };
  },
  computed: {
    showDistributionListDropdown() {
      return (
        this.$permissions.has("can_use_distribution_lists") || this.showDropdown
      );
    },
    visibleDistributionLists() {
      if (this.currentUser.role === "super") return this.distributionLists;

      return this.distributionLists.filter((list) => !list.restricted);
    },
  },
  watch: {
    modelValue: {
      handler(newValue) {
        if (newValue && this.recipients !== newValue) {
          this.recipients = [...newValue];
        }
      },
    },
    listFilter: {
      handler(newValue) {
        this.loadDistributionLists(this.currentUser.organisation.id, newValue);
      },
    },
  },
  created() {
    this.loadDistributionLists(this.currentUser.organisation.id);
  },
  mounted() {
    this.recipients = this.modelValue;
  },
  methods: {
    async loadDistributionLists(organisationId, searchValue = "") {
      try {
        const response = await DistributionList.where({ organisationId })
          .where({
            list_and_recipients: { match: searchValue },
            ...(this.$features.has("has_team_workspaces")
              ? { organisationTeamId: [this.selectedTeamPreference, "null"] }
              : {}),
          })
          .per(999)
          .all();

        this.distributionLists = response.data;
      } catch (error) {
        this.$error(
          this.$t("distributed_email_input.load_distribution_lists_error"),
          error
        );
      }
    },
    forceUpdate() {
      this.$refs.emailField.forceUpdate();

      return this.$nextTick().then(() => {
        this.$emit("update:modelValue", this.recipients);
      });
    },
    addFromDistributionList(distributionList) {
      const newRecipients = this.recipients.slice(0);
      newRecipients.push(distributionList.referenceEmail);
      this.recipients = newRecipients;
      this.$emit("update:modelValue", this.recipients);
    },
    onUpdateRecipients() {
      this.forceUpdate();
    },
    clearRecipients() {
      this.recipients = [];
      this.onUpdateRecipients();
    },
    /** @public */
    validate() {
      if (!this.validateField) return true;

      this.displayError = false;
      const valid = Boolean(this.recipients.length);
      if (!valid) this.displayError = true;

      return valid;
    },
    /** @public */
    focus() {
      this.$refs.emailField.focus();
    },
  },
};
</script>

<style lang="scss" scoped>
.error {
  color: #b60000;
  font-weight: normal;
  font-size: 12px;
}

.recipients-email-field {
  width: 100%;
  margin-bottom: 4px;
}

.tag {
  background-color: #316899;
  border-radius: 9px;
  padding: 2px 10px;
  font-size: 10px;
}

.clear-recipients-button {
  margin-top: 16px;
  align-self: start;
}

.dropdown-list {
  padding: 0.285em 0;
  max-height: 500px;

  .item {
    flex-direction: column;
    align-items: start;

    small {
      margin-top: 2px;
    }
  }
}

.new-design {
  .recipients-email-field {
    margin-bottom: 0;
  }
}
</style>
